<template>
    <modal @close="closeWindow" dialog="modal-lg" :class="['reservation-modal dark-top refund-transaction-modal', { 'refund-transaction-modal--scope': currentStep === STEP.SCOPE }]">
        <div slot="title">
            <h2>Payment Refund</h2>
        </div>

        <div slot="body">
            <!-- Step progress bar -->
            <div class="refund__steps px-4 pt-3 pb-1">
                <div
                    v-for="(step, index) in STEPS"
                    :key="step.id"
                    class="refund__step-group"
                >
                    <div
                        class="refund__step"
                        :class="{ 'refund__step--active': currentStep === step.id, 'refund__step--done': currentStep > step.id }"
                    >
                        <span class="refund__step-number">
                            <template v-if="currentStep > step.id">&#10003;</template>
                            <template v-else>{{ step.id }}</template>
                        </span>
                        <span class="refund__step-label">{{ step.label }}</span>
                    </div>
                    <span v-if="index < STEPS.length - 1" class="refund__step-line" :class="{ 'refund__step-line--done': currentStep > step.id }"></span>
                </div>
            </div>

            <!-- Step 1: Refund scope -->
            <step1-scope
                v-if="currentStep === STEP.SCOPE"
                :value="selectedScope"
                :subStep="scopeSubStep"
                :payments="transactionPayments"
                :selectedPaymentId="selectedPaymentId"
                :reservation="getSelectedReservation"
                @input="onScopeSelected"
                @select-payment="selectedPaymentId = $event"
            />

            <!-- Step 2: Refund details -->
            <step2-details
                v-else
                ref="detailsStep"
                :selected-scope="selectedScope"
                :selected-payment="selectedPayment"
                :scope-total="scopeTotal"
                :valid.sync="detailsValid"
                :submitting.sync="detailsSubmitting"
                :email-warning.sync="detailsEmailWarning"
            />
        </div>

        <div slot="footer" class="align-items-center">
            <div v-if="currentStep === STEP.DETAILS" class="color-6 w-100 text-align-right">
                {{ detailsEmailWarning }}
            </div>
            <button v-if="showCancel" type="button" class="btn cancel" :disabled="detailsSubmitting" @click="closeWindow">Cancel</button>
            <button v-else type="button" class="btn blue ok" :disabled="detailsSubmitting" @click="goBack">
                <i class="fas fa-chevron-left mr-1"></i> Back
            </button>
            <button
                type="button"
                :class="['btn ok', currentStep === STEP.DETAILS ? 'green' : 'blue']"
                :disabled="isNextDisabled"
                @click="nextStep"
            >
                <loader v-if="detailsSubmitting" size="small" />
                <template v-else-if="currentStep === STEP.DETAILS">Confirm</template>
                <template v-else>Next <i class="fas fa-chevron-right ml-1"></i></template>
            </button>
        </div>
    </modal>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import Modal from './Modal'
    import Loader from '@/tools/loaders/loader1'

    import Step1Scope from './ReservationRefundTransaction/Step1Scope'
    import Step2Details from './ReservationRefundTransaction/Step2Details'
    import { REFUND_SCOPE, STEP, STEPS, SCOPE_SUBSTEP } from '@/utils/refundHelpers'

    import { reservationsMixin } from '@/mixins/reservations'

    export default {
        mixins: [ reservationsMixin ],
        created() {
            // Capture the scope context set by the caller (payIn.vue): a grouped transaction
            // sets a transaction code, a standalone payment sets a payment id.
            this.contextTransactionCode = this.getRefundTransactionCode;
            this.contextPaymentId = this.getRefundPaymentId;
            this.transactionPayments = this.buildTransactionPayments();

            if (this.transactionPayments.length === 1) {
                this.selectedScope = REFUND_SCOPE.ALL_PAYMENTS;
            }
        },
        beforeDestroy() {
            this.setRefundPaymentId(null);
            this.setRefundTransactionCode(null);
        },
        data() {
            return {
                REFUND_SCOPE,
                STEP,
                STEPS,
                SCOPE_SUBSTEP,
                currentStep: STEP.SCOPE,
                selectedScope: null,
                scopeSubStep: SCOPE_SUBSTEP.CARDS,
                selectedPaymentId: null,
                contextTransactionCode: null,
                contextPaymentId: null,
                transactionPayments: [],
                // Footer state synced up from the details step.
                detailsValid: false,
                detailsSubmitting: false,
                detailsEmailWarning: '',
            }
        },
        computed: {
            ...mapGetters([
                'getSelectedReservation',
                'getIsLoadingCalculations',
                'getRefundPaymentId',
                'getRefundTransactionCode',
            ]),
            scopeTotal() {
                return this.transactionPayments.reduce((sum, payment) => sum + (payment.paidValue || 0), 0);
            },
            selectedPayment() {
                return this.transactionPayments.find(payment => payment.id === this.selectedPaymentId) || null;
            },
            showCancel() {
                return this.currentStep === STEP.SCOPE && this.scopeSubStep === SCOPE_SUBSTEP.CARDS;
            },
            isNextDisabled() {
                if (this.detailsSubmitting) return true;
                if (this.currentStep === STEP.SCOPE) {
                    if (this.scopeSubStep === SCOPE_SUBSTEP.CARDS) return !this.selectedScope;
                    return this.selectedPaymentId == null;
                }
                return this.getIsLoadingCalculations || this.detailsValid === false;
            },
        },
        methods: {
            ...mapActions([
                'closeModal',
                'setReservationCalculations',
                'setRefundPaymentId',
                'setRefundTransactionCode',
            ]),
            buildTransactionPayments() {
                const payments = this.getSelectedReservation.payments || [];

                if (this.contextTransactionCode != null) {
                    return payments.filter(p => p.transactionCode === this.contextTransactionCode);
                }

                if (this.contextPaymentId != null) {
                    const found = payments.find(p => p.id === this.contextPaymentId);
                    return found ? [found] : [];
                }

                return [];
            },
            onScopeSelected(scope) {
                this.selectedScope = scope;
            },
            // A grouped transaction refunds by transaction code; everything else refunds by a
            // single payment id. The two are mutually exclusive in the store.
            applyScope() {
                if (this.selectedScope === REFUND_SCOPE.ALL_PAYMENTS && this.contextTransactionCode != null) {
                    this.setRefundTransactionCode(this.contextTransactionCode);
                    this.setRefundPaymentId(null);
                } else if (this.selectedScope === REFUND_SCOPE.ALL_PAYMENTS) {
                    this.setRefundPaymentId(this.contextPaymentId);
                    this.setRefundTransactionCode(null);
                } else {
                    this.setRefundPaymentId(this.selectedPaymentId);
                    this.setRefundTransactionCode(null);
                }
            },
            nextStep() {
                if (this.currentStep === STEP.DETAILS) {
                    this.$refs.detailsStep.submit();
                    return;
                }

                if (this.scopeSubStep === SCOPE_SUBSTEP.CARDS && this.selectedScope === REFUND_SCOPE.SELECT_PAYMENT) {
                    this.scopeSubStep = SCOPE_SUBSTEP.PAYMENTS;
                    return;
                }

                // Commit the scope to the store before the details step mounts and reads it.
                this.applyScope();
                this.currentStep = STEP.DETAILS;
            },
            goBack() {
                if (this.currentStep === STEP.DETAILS) {
                    this.currentStep = STEP.SCOPE;
                    this.scopeSubStep = this.selectedScope === REFUND_SCOPE.SELECT_PAYMENT
                        ? SCOPE_SUBSTEP.PAYMENTS
                        : SCOPE_SUBSTEP.CARDS;
                    return;
                }
                this.scopeSubStep = SCOPE_SUBSTEP.CARDS;
            },
            closeWindow() {
                this.setRefundPaymentId(null);
                this.setRefundTransactionCode(null);
                this.setReservationCalculations(this.getSelectedReservation);
                this.closeModal();
            },
        },
        components: {
            Modal,
            Loader,
            Step1Scope,
            Step2Details,
        }
    }
</script>

<style lang="scss">
    @import '../../assets/scss/_variables.scss';

    .refund-transaction-modal {
        align-items: flex-start;
        padding-top: 3rem;
    }

    .refund-transaction-modal .modal-dialog {
        width: 100%;
        max-width: 760px;
        transition: max-width 0.3s ease;
    }

    .refund-transaction-modal--scope .modal-dialog {
        max-width: 650px;
    }

    .refund-transaction-modal .modal-content {
        display: flex;
        flex-direction: column;
        max-height: calc(100vh - 7rem);
    }

    .refund-transaction-modal .modal-header,
    .refund-transaction-modal .modal-footer {
        flex-shrink: 0;
    }

    .refund-transaction-modal .modal-body {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;

        scrollbar-gutter: stable;

        /* Visible scrollbar so users can tell there is more content */
        scrollbar-width: thin;                 /* Firefox */
        scrollbar-color: $medium transparent;  /* Firefox */

        &::-webkit-scrollbar {                 /* Chrome / Safari */
            width: 0.5rem;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background-color: $medium;
            border-radius: 0.25rem;
        }
    }
</style>

<style lang="scss" scoped>
    @import "~@/assets/scss/_utility.scss";
    @import "~@/assets/scss/_variables.scss";

    // ----- Step indicator -----
    .refund__steps {
        display: flex;
        align-items: flex-start;
        justify-content: center;
        margin-top: 0.75rem;
        padding-bottom: 0.25rem;
    }

    .refund__step-group {
        display: contents;
    }

    .refund__step {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        color: $dark;
        font-size: 0.85rem;
        white-space: nowrap;
    }

    .refund__step--active {
        color: $darkest;
        font-weight: 600;
    }

    .refund__step--active .refund__step-number {
        background: $color-1;
        border-color: $color-1;
        color: $lightest;
    }

    .refund__step--done .refund__step-number {
        background: $color-3;
        border-color: $color-3;
        color: $lightest;
    }

    .refund__step-number {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        width: 1.8rem;
        height: 1.8rem;
        border-radius: 50%;
        border: 1px solid $dark;
        font-size: 0.85rem;
        font-weight: 600;
        flex-shrink: 0;
    }

    .refund__step-line {
        flex: 1;
        height: 1px;
        background: $dark;
        min-width: 2.5rem;
        margin: 0.9rem 0.5rem 0;
        opacity: 0.4;

        &--done {
            background: $color-3;
            opacity: 1;
        }
    }
</style>



// WEBPACK FOOTER //
// ReservationRefundTransaction.vue?b06107ee